import React from 'react';
import { Card, Typography, Tooltip, Button } from "antd";
import Map, { Source, Layer, Popup, NavigationControl } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_TOKEN } from './utils';
import { CompassOutlined } from '@ant-design/icons';

const { Text } = Typography;

const CustomerLocationMap = ({ 
  viewState, 
  setViewState, 
  geoJsonData, 
  popupInfo, 
  setPopupInfo 
}) => {
  console.log('CustomerLocationMap rendering with geoJsonData:', geoJsonData);
  
  // Check if we have valid data to render
  const hasValidData = geoJsonData && 
                      geoJsonData.features && 
                      Array.isArray(geoJsonData.features) && 
                      geoJsonData.features.length > 0;
                      
  console.log(`Map has valid data to render: ${hasValidData}`);
  if (!hasValidData) {
    console.warn('No valid GeoJSON data available for the map');
  }
  return (
    <Card 
      style={{  width: '100%' }}
 
    >
      <div style={{ height: 700, border: '1px solid #f0f0f0', borderRadius: 1, position: 'relative' }}>
        <Map
          {...viewState}
          onMove={evt => {
            // Always keep USA centered in the view
            // Define the center point of USA
            const usaCenter = {  longitude: -98.5795,
              latitude: 39.8283,  zoom: 3.8 };
            
            // Calculate how far we can deviate from the center based on zoom level
            // At higher zoom levels, allow more movement
            const zoomFactor = Math.min(1, 0.2 + (evt.viewState.zoom - 2) * 0.1);
            const maxLongDeviation = 25 * zoomFactor;
            const maxLatDeviation = 15 * zoomFactor;
            
            // Restrict movement to keep USA centered
            const newViewState = {
              ...evt.viewState,
              // Keep longitude centered around USA's center
              longitude: Math.max(
                usaCenter.longitude - maxLongDeviation, 
                Math.min(usaCenter.longitude + maxLongDeviation, evt.viewState.longitude)
              ),
              // Keep latitude centered around USA's center
              latitude: Math.max(
                usaCenter.latitude - maxLatDeviation, 
                Math.min(usaCenter.latitude + maxLatDeviation, evt.viewState.latitude)
              ),
              // Restrict zoom level
              zoom: Math.max(2, Math.min(12, evt.viewState.zoom))
            };
            setViewState(newViewState);
          }}
          mapStyle="mapbox://styles/mapbox/light-v11" // Light map style for better visibility
          mapboxAccessToken={MAPBOX_TOKEN}
          style={{ width: '100%', height: '100%' }}
          onClick={() => setPopupInfo(null)}
          maxBounds={[[-125, 24], [-66, 50]]} // Set max bounds to continental USA
          interactiveLayerIds={['city-points']}
          onMouseMove={e => {
            if (e.features && e.features.length > 0) {
              const feature = e.features[0];
              const props = feature.properties;
              
              // Need to parse JSON strings from properties
              const parsedProps = {
                city: props.city,
                state: props.state,
                customer_count: parseInt(props.customer_count),
                total_sales: parseFloat(props.total_sales),
                order_count: parseInt(props.order_count),
                avg_order_value: parseFloat(props.avg_order_value),
                orders_per_customer: parseFloat(props.orders_per_customer),
                repeat_purchase_rate: parseFloat(props.repeat_purchase_rate)
              };
              
              setPopupInfo({
                longitude: feature.geometry.coordinates[0],
                latitude: feature.geometry.coordinates[1],
                ...parsedProps
              });
            }
          }}
          getCursor={({isHovering}) => isHovering ? 'pointer' : 'default'}
        >
          {/* Add navigation controls */}
          <NavigationControl position="top-right" />
   
          
          {geoJsonData && (
            <Source
              type="geojson"
              data={geoJsonData}
            >
              <Layer
                id="city-points"
                type="circle"
                paint={{
                  // Dynamic circle radius based on customer count and zoom level
                  'circle-radius': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    3, ['*', 1.0, ['sqrt', ['get', 'customer_count']]],  // Smaller at far zoom
                    6, ['*', 1.5, ['sqrt', ['get', 'customer_count']]],  // Medium at medium zoom
                    10, ['*', 2.0, ['sqrt', ['get', 'customer_count']]]   // Larger at close zoom
                  ],
                  // Add a stroke around circles for better visibility
                  'circle-stroke-width': 1,
                  'circle-stroke-color': '#ffffff',
                  // Dynamic color based on orders per customer using interpolation for smoother transitions
                  'circle-color': [
                    'interpolate',
                    ['linear'],
                    ['get', 'orders_per_customer'],
                    1, '#FF3D00',   // 1 order per customer - red
                    2, '#FFD600',   // 2 orders per customer - yellow
                    3, '#00C853',   // 3 orders per customer - green
                    5, '#00796B'    // 5+ orders per customer - teal (for exceptional performance)
                  ],
                  // Circle opacity based on customer count for visual emphasis
                  'circle-opacity': [
                    'interpolate',
                    ['linear'],
                    ['get', 'customer_count'],
                    1, 0.7,    // Single customer - more transparent
                    10, 0.8,   // 10 customers - medium opacity
                    50, 0.9    // 50+ customers - more opaque
                  ]
                }}
              />
            </Source>
          )}
          
          {popupInfo && (
            <Popup
              longitude={popupInfo.longitude}
              latitude={popupInfo.latitude}
              anchor="bottom"
              onClose={() => setPopupInfo(null)}
              closeOnClick={false}
            >
              <div style={{ padding: '10px', maxWidth: '280px' }}>
                <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', borderBottom: '1px solid #f0f0f0', paddingBottom: '4px' }}>
                  {popupInfo.city}, {popupInfo.state}
                </div>
                
                {/* Key metrics with visual indicators */}
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '13px' }}>Orders per Customer:</span>
                    <span style={{ 
                      fontWeight: 'bold', 
                      color: popupInfo.orders_per_customer >= 3 ? '#00C853' : popupInfo.orders_per_customer >= 2 ? '#FFD600' : '#FF3D00',
                      fontSize: '14px'
                    }}>
                      {popupInfo.orders_per_customer.toFixed(1)}
                    </span>
                  </div>
                  <div style={{ 
                    height: '4px', 
                    background: '#f0f0f0', 
                    borderRadius: '2px', 
                    overflow: 'hidden' 
                  }}>
                    <div style={{ 
                      width: `${Math.min(100, popupInfo.orders_per_customer * 20)}%`, 
                      height: '100%', 
                      background: popupInfo.orders_per_customer >= 3 ? '#00C853' : popupInfo.orders_per_customer >= 2 ? '#FFD600' : '#FF3D00'
                    }} />
                  </div>
                </div>
                
                {/* Customer metrics */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px', marginBottom: '8px' }}>
                  <div>
                    <div style={{ color: '#8c8c8c', marginBottom: '2px' }}>Customers</div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{popupInfo.customer_count.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ color: '#8c8c8c', marginBottom: '2px' }}>Repeat Rate</div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{popupInfo.repeat_purchase_rate.toFixed(1)}%</div>
                  </div>
                </div>
                
                {/* Financial metrics */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                  <div>
                    <div style={{ color: '#8c8c8c', marginBottom: '2px' }}>Total Sales</div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>${popupInfo.total_sales.toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                  </div>
                  <div>
                    <div style={{ color: '#8c8c8c', marginBottom: '2px' }}>Total Orders</div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{popupInfo.order_count.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ color: '#8c8c8c', marginBottom: '2px' }}>Avg Order Value</div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>${popupInfo.avg_order_value.toFixed(2)}</div>
                  </div>
                  <div>
                    <div style={{ color: '#8c8c8c', marginBottom: '2px' }}>Orders/Customer</div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{popupInfo.orders_per_customer.toFixed(1)}</div>
                  </div>
                </div>
              </div>
            </Popup>
          )}
        </Map>
      </div>
    </Card>
  );
};

export default CustomerLocationMap;
